import React from 'react'
import Styles from './OverviewInfoCard.module.css'
import { Colors } from 'Theme'
import PropTypes from 'prop-types'
import IFText from 'Components/IFText/IFText'
import { useTranslation } from 'react-i18next'
import IFsvg from 'Components/IFsvg/IFsvg'
import OverviewInfoCardSkeleton from './OverviewInfoCardSkeleton'
import { formatNumber, getEnergyUnit } from 'Utils/Calculations'

const OverviewInfoCard = ({
  chargingSessions,
  chargingSessionsPercentage,
  energyConsumed,
  energyConsumedPercentage,
  averageConsumption,
  averageConsumptionPercentage,
  averageDuration,
  averageDurationPercentage,
  averageCost,
  averageCostPercentage,
  transactionRevenue,
  transactionRevenuePercentage,
  isLoading,
  isRevenue = false,
  currency,
}) => {
  const { t } = useTranslation()
  const { divisor, unit } = getEnergyUnit(energyConsumed)

  return (
    <div
      style={{
        backgroundColor: Colors.white,
      }}
      className={Styles.Card}
    >
      {isLoading ? (
        <OverviewInfoCardSkeleton />
      ) : (
        <div className={Styles.Container}>
          <div className={Styles.Section}>
            <div className={Styles.Top}>
              <IFsvg.EvStation />
              <IFText
                style={{ color: Colors.Analytics.Icons }}
                className={Styles.Title}
              >
                {t('Analytics.ChargingSessions')}
              </IFText>
            </div>
            <div>
              <IFText style={{ color: Colors.black }} className={Styles.Value}>
                {chargingSessions?.toLocaleString()}
              </IFText>
              <div className={Styles.Top}>
                <div
                  className={Styles.ArrowContainer}
                  style={{
                    backgroundColor:
                      chargingSessionsPercentage > 0
                        ? Colors.Analytics.LightGreen
                        : Colors.Analytics.LightRed,
                  }}
                >
                  <IFsvg.ArrowOutward
                    transform={
                      chargingSessionsPercentage > 0 ? 'none' : 'rotate(90deg)'
                    }
                    className={Styles.Arrow}
                    fill={
                      chargingSessionsPercentage > 0
                        ? Colors.Analytics.GreenPercentage
                        : Colors.red
                    }
                    height={16}
                    width={16}
                  />
                </div>
                <IFText
                  style={{
                    color:
                      chargingSessionsPercentage > 0
                        ? Colors.Analytics.GreenPercentage
                        : Colors.red,
                  }}
                  className={Styles.Percentage}
                >
                  {`${Math.abs(
                    parseFloat(chargingSessionsPercentage?.toFixed(1)),
                  ).toLocaleString()}%`}
                </IFText>
              </div>
            </div>
          </div>

          {isRevenue ? (
            <div className={Styles.Section}>
              <div className={Styles.Top}>
                <IFsvg.Cheque />
                <IFText
                  style={{ color: Colors.Analytics.Icons }}
                  className={Styles.Title}
                >
                  {t('Analytics.Revenue')}
                </IFText>
              </div>
              <div>
                <div className={Styles.Top}>
                  <IFText
                    style={{ color: Colors.black }}
                    className={Styles.Value}
                  >
                    {`${formatNumber(transactionRevenue)}`}
                  </IFText>
                  <IFText className={Styles.Unit}>{currency}</IFText>
                </div>
                <div className={Styles.Top}>
                  <div
                    className={Styles.ArrowContainer}
                    style={{
                      backgroundColor:
                        transactionRevenuePercentage > 0
                          ? Colors.Analytics.LightGreen
                          : Colors.Analytics.LightRed,
                    }}
                  >
                    <IFsvg.ArrowOutward
                      transform={
                        transactionRevenuePercentage > 0
                          ? 'none'
                          : 'rotate(90deg)'
                      }
                      className={Styles.Arrow}
                      fill={
                        transactionRevenuePercentage > 0
                          ? Colors.Analytics.GreenPercentage
                          : Colors.red
                      }
                      height={16}
                      width={16}
                    />
                  </div>
                  <IFText
                    style={{
                      color:
                        transactionRevenuePercentage > 0
                          ? Colors.Analytics.GreenPercentage
                          : Colors.red,
                    }}
                    className={Styles.Percentage}
                  >
                    {`${Math.abs(
                      parseFloat(transactionRevenuePercentage?.toFixed(1)),
                    ).toLocaleString()}%`}
                  </IFText>
                </div>
              </div>
            </div>
          ) : (
            <div className={Styles.Section}>
              <div className={Styles.Top}>
                <IFsvg.Database />
                <IFText
                  style={{ color: Colors.Analytics.Icons }}
                  className={Styles.Title}
                >
                  {t('Analytics.Consumed')}
                </IFText>
              </div>
              <div>
                <div className={Styles.Top}>
                  <IFText
                    style={{ color: Colors.black }}
                    className={Styles.Value}
                  >
                    {`${Math.abs(
                      parseFloat(energyConsumed / divisor?.toFixed(1)),
                    ).toLocaleString()}`}
                  </IFText>
                  <IFText className={Styles.Unit}>{unit}</IFText>
                </div>
                <div className={Styles.Top}>
                  <div
                    className={Styles.ArrowContainer}
                    style={{
                      backgroundColor:
                        energyConsumedPercentage > 0
                          ? Colors.Analytics.LightGreen
                          : Colors.Analytics.LightRed,
                    }}
                  >
                    <IFsvg.ArrowOutward
                      transform={
                        energyConsumedPercentage > 0 ? 'none' : 'rotate(90deg)'
                      }
                      className={Styles.Arrow}
                      fill={
                        energyConsumedPercentage > 0
                          ? Colors.Analytics.GreenPercentage
                          : Colors.red
                      }
                      height={16}
                      width={16}
                    />
                  </div>
                  <IFText
                    style={{
                      color:
                        energyConsumedPercentage > 0
                          ? Colors.Analytics.GreenPercentage
                          : Colors.red,
                    }}
                    className={Styles.Percentage}
                  >
                    {`${Math.abs(
                      parseFloat(energyConsumedPercentage?.toFixed(1)),
                    ).toLocaleString()}%`}
                  </IFText>
                </div>
              </div>
            </div>
          )}

          {isRevenue ? (
            <div className={Styles.Section}>
              <div className={Styles.Top}>
                <IFsvg.Payment />
                <IFText
                  style={{ color: Colors.Analytics.Icons }}
                  className={Styles.Title}
                >
                  {t('Analytics.Cost')}
                </IFText>
              </div>
              <div>
                <div className={Styles.Top}>
                  <IFText
                    style={{ color: Colors.black }}
                    className={Styles.Value}
                  >
                    {`${formatNumber(averageCost)}`}
                  </IFText>
                  <IFText className={Styles.Unit}>{currency}</IFText>
                </div>
                <div className={Styles.Top}>
                  <div
                    className={Styles.ArrowContainer}
                    style={{
                      backgroundColor:
                        averageCostPercentage > 0
                          ? Colors.Analytics.LightGreen
                          : Colors.Analytics.LightRed,
                    }}
                  >
                    <IFsvg.ArrowOutward
                      transform={
                        averageCostPercentage > 0 ? 'none' : 'rotate(90deg)'
                      }
                      className={Styles.Arrow}
                      fill={
                        averageCostPercentage > 0
                          ? Colors.Analytics.GreenPercentage
                          : Colors.red
                      }
                      height={16}
                      width={16}
                    />
                  </div>
                  <IFText
                    style={{
                      color:
                        averageCostPercentage > 0
                          ? Colors.Analytics.GreenPercentage
                          : Colors.red,
                    }}
                    className={Styles.Percentage}
                  >
                    {`${Math.abs(
                      parseFloat(averageCostPercentage?.toFixed(1)),
                    ).toLocaleString()}%`}
                  </IFText>
                </div>
              </div>
            </div>
          ) : (
            <div className={Styles.Section}>
              <div className={Styles.Top}>
                <IFsvg.MobileCharge />
                <IFText
                  style={{ color: Colors.Analytics.Icons }}
                  className={Styles.Title}
                >
                  {t('Analytics.AverageConsumption')}
                </IFText>
              </div>
              <div>
                <div className={Styles.Top}>
                  <IFText
                    style={{ color: Colors.black }}
                    className={Styles.Value}
                  >
                    {`${Math.abs(
                      parseFloat(averageConsumption?.toFixed(1)),
                    ).toLocaleString()}`}
                  </IFText>
                  <IFText className={Styles.Unit}>
                    {t('Analytics.KiloWattHour')}
                  </IFText>
                </div>
                <div className={Styles.Top}>
                  <div
                    className={Styles.ArrowContainer}
                    style={{
                      backgroundColor:
                        averageConsumptionPercentage > 0
                          ? Colors.Analytics.LightGreen
                          : Colors.Analytics.LightRed,
                    }}
                  >
                    <IFsvg.ArrowOutward
                      transform={
                        averageConsumptionPercentage > 0
                          ? 'none'
                          : 'rotate(90deg)'
                      }
                      className={Styles.Arrow}
                      fill={
                        averageConsumptionPercentage > 0
                          ? Colors.Analytics.GreenPercentage
                          : Colors.red
                      }
                      height={16}
                      width={16}
                    />
                  </div>
                  <IFText
                    style={{
                      color:
                        averageConsumptionPercentage > 0
                          ? Colors.Analytics.GreenPercentage
                          : Colors.red,
                    }}
                    className={Styles.Percentage}
                  >
                    {`${Math.abs(
                      parseFloat(averageConsumptionPercentage?.toFixed(1)),
                    ).toLocaleString()}%`}
                  </IFText>
                </div>
              </div>
            </div>
          )}

          <div className={Styles.Section}>
            <div className={Styles.Top}>
              <IFsvg.AverageTime />
              <IFText
                style={{ color: Colors.Analytics.Icons }}
                className={Styles.Title}
              >
                {t('Analytics.AverageDuration')}
              </IFText>
            </div>
            <div>
              <div className={Styles.Top}>
                <IFText
                  style={{ color: Colors.black }}
                  className={Styles.Value}
                >
                  {`${Math.abs(
                    parseFloat(averageDuration?.toFixed(1)),
                  ).toLocaleString()}`}
                </IFText>
                <IFText className={Styles.Unit}>{t('Analytics.Hours')}</IFText>
              </div>
              <div className={Styles.Top}>
                <div
                  className={Styles.ArrowContainer}
                  style={{
                    backgroundColor:
                      averageDurationPercentage > 0
                        ? Colors.Analytics.LightGreen
                        : Colors.Analytics.LightRed,
                  }}
                >
                  <IFsvg.ArrowOutward
                    transform={
                      averageDurationPercentage > 0 ? 'none' : 'rotate(90deg)'
                    }
                    className={Styles.Arrow}
                    fill={
                      averageDurationPercentage > 0
                        ? Colors.Analytics.GreenPercentage
                        : Colors.red
                    }
                    height={16}
                    width={16}
                  />
                </div>
                <IFText
                  style={{
                    color:
                      averageDurationPercentage > 0
                        ? Colors.Analytics.GreenPercentage
                        : Colors.red,
                  }}
                  className={Styles.Percentage}
                >
                  {`${Math.abs(
                    parseFloat(averageDurationPercentage?.toFixed(1)),
                  ).toLocaleString()}%`}
                </IFText>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

OverviewInfoCard.propTypes = {
  chargingSessions: PropTypes.number,
  chargingSessionsPercentage: PropTypes.number,
  energyConsumed: PropTypes.number,
  energyConsumedPercentage: PropTypes.number,
  averageConsumption: PropTypes.number,
  averageConsumptionPercentage: PropTypes.number,
  averageDuration: PropTypes.number,
  averageDurationPercentage: PropTypes.number,
  averageCost: PropTypes.number,
  averageCostPercentage: PropTypes.number,
  transactionRevenue: PropTypes.number,
  transactionRevenuePercentage: PropTypes.number,
  isLoading: PropTypes.bool,
  isRevenue: PropTypes.bool,
  currency: PropTypes.string,
}

export default OverviewInfoCard
